import axios from "axios";
import DataGrid, {
  Column,
  Editing,
  Form,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  Texts,
} from "devextreme-react/data-grid";
import { Label, RequiredRule, SimpleItem } from "devextreme-react/form";
import CustomStore from "devextreme/data/custom_store";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { kForm, kFormList } from "./data";
import TwoDateBoxComponent2 from "./TwoDateBoxComponent2";

export default function ContractForm() {
  const [dataSource, setDataSource] = useState(null);

  const [formDataSource, setFormDataSource] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const kStartDateRef = useRef(null);
  const kEndDateRef = useRef(null);
  const probationaryStartDateRef = useRef(null);
  const probationaryEndDateRef = useRef(null);

  const contractFormGridRef = useRef(null);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/contrForm/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          values = {
            ...values,
            kStartDate: kStartDateRef.current.instance.option("value"),
            kEndDate: kEndDateRef.current.instance.option("value"),
            probationaryStartDate:
              probationaryStartDateRef.current.instance.option("value"),
            probationaryEndDate:
              probationaryEndDateRef.current.instance.option("value"),
          };

          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/contrForm/${userInfo.employeeNo}`,
            values
          );
        },
        update: async (key, values) => {
          values = {
            ...values,
            kStartDate: kStartDateRef.current.instance.option("value"),
            kEndDate: kEndDateRef.current.instance.option("value"),
            probationaryStartDate:
              probationaryStartDateRef.current.instance.option("value"),
            probationaryEndDate:
              probationaryEndDateRef.current.instance.option("value"),
          };

          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/contrForm/${userInfo.employeeNo}/${key}`,
            values
          );
        },

        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/contrForm/${userInfo.employeeNo}/${key}`
          );
        },
        errorHandler: (error) => {
          if (error.message == "Request failed with status code 409") {
            error.message = `The contract form is duplicated.`;
          }
        },
      })
    );
  }, []);

  const calculateDisplayValue = (data) => {
    return kForm[data.kForm];
  };

  const onEditingStart = (e) => {
    if (e.data.kStartDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        kStartDate: e.data.kStartDate,
      }));
    }

    if (e.data.kEndDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        kEndDate: e.data.kEndDate,
      }));
    }

    if (e.data.probationaryStartDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        probationaryStartDate: e.data.probationaryStartDate,
      }));
    }

    if (e.data.probationaryEndDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        probationaryEndDate: e.data.probationaryEndDate,
      }));
    }
  };

  const onSaving = React.useCallback(async (e) => {
    if (e.changes.length == 0) {
      const SEQ =
        contractFormGridRef.current.instance.option("editing.editRowKey");

      const values = {
        kStartDate: kStartDateRef.current.instance.option("value"),
        kEndDate: kEndDateRef.current.instance.option("value"),
        probationaryStartDate:
          probationaryStartDateRef.current.instance.option("value"),
        probationaryEndDate:
          probationaryEndDateRef.current.instance.option("value"),
      };

      await axios.put(
        `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/contrForm/${userInfo.employeeNo}/${SEQ}`,
        values
      );

      contractFormGridRef.current.instance.refresh();
    }
  }, []);

  const onInitNewRow = (e) => {
    const key = contractFormGridRef.current.instance.getKeyByRowIndex(0);

    if (key) {
      e.component.selectRows(key, true);
      e.data = contractFormGridRef.current.instance.getSelectedRowsData()[0];

      if (e.data.kStartDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          kStartDate: e.data.kStartDate,
        }));
      }

      if (e.data.kEndDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          kEndDate: e.data.kEndDate,
        }));
      }

      if (e.data.probationaryStartDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          probationaryStartDate: e.data.probationaryStartDate,
        }));
      }

      if (e.data.probationaryEndDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          probationaryEndDate: e.data.probationaryEndDate,
        }));
      }
    }
  };

  return (
    <DataGrid
      id="contractFormGrid"
      ref={contractFormGridRef}
      dataSource={dataSource}
      keyExpr="SEQ"
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
      }}
      hoverStateEnabled={true}
      showColumnHeaders={true}
      onEditingStart={onEditingStart}
      onSaving={onSaving}
      onInitNewRow={onInitNewRow}
    >
      <LoadPanel enabled={true} />

      <SearchPanel visible={true} />

      <Scrolling mode="virtual" showScrollbar={"always"} />

      <Selection mode="single" />

      <Editing
        mode={"popup"}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
      >
        <Texts
          confirmDeleteTitle={"Delete the Contract Form"}
          confirmDeleteMessage="Are you sure you want to delete this contract form?"
        />
        <Popup
          title="Contract Form"
          showTitle={true}
          height={"auto"}
          width={1200}
        />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="contractFormGroup"
          height={"100%"}
        >
          <SimpleItem
            dataField={"apptDate"}
            editorType="dxDateBox"
            editorOptions={{
              type: "date",
              displayFormat: "yyyy-MM-dd",
              dateSerializationFormat: "yyyy-MM-dd",
              showClearButton: true,
            }}
          >
            {/* <Label text={"발령일"} /> */}
            <Label text={"Appointment Date"} />
            <RequiredRule message={"* Appointment date is required."} />
          </SimpleItem>
          <SimpleItem
            dataField={"kForm"}
            editorType="dxSelectBox"
            editorOptions={{
              dataSource: kFormList,
              valueExpr: "id",
              displayExpr: "name",
              showClearButton: true,
            }}
          >
            {/* <Label text={"계약 형태"} /> */}
            <Label text={"Contract Type"} />
            {/* <RequiredRule message={"* 계약 형태를 입력해주세요."} /> */}
            <RequiredRule message={"* Contract type is required."} />
          </SimpleItem>
          <SimpleItem>
            {/* <Label text={"계약기간"} /> */}
            <Label text={"Contract Period"} />
            <TwoDateBoxComponent2
              firstDateBox={{
                // placeholder: "계약시작일",
                placeholder: "Contract Start Date",
                field: "kStartDate",
                ref: kStartDateRef,
              }}
              secondDateBox={{
                // placeholder: "계약종료일",
                placeholder: "Contract End Date",
                field: "kEndDate",
                ref: kEndDateRef,
              }}
              // setFormDataSource={setFormDataSource}
              formDataSource={formDataSource}
            />
          </SimpleItem>
          <SimpleItem>
            {/* <Label text={"수습기간"} /> */}
            <Label text={"Probation Period"} />
            <TwoDateBoxComponent2
              firstDateBox={{
                // placeholder: "수습시작일",
                placeholder: "Probation Start Date",
                field: "probationaryStartDate",
                ref: probationaryStartDateRef,
              }}
              secondDateBox={{
                // placeholder: "수습종료일",
                placeholder: "Probation End Date",
                field: "probationaryEndDate",
                ref: probationaryEndDateRef,
              }}
              // setFormDataSource={setFormDataSource}
              formDataSource={formDataSource}
            />
          </SimpleItem>
        </Form>
      </Editing>

      <Column
        dataField={"apptDate"}
        // caption="발령일"
        caption="Appointment Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={150}
        alignment={"center"}
      />
      <Column
        dataField={"kForm"}
        // caption="계약형태"
        caption="Contract Type"
        calculateDisplayValue={calculateDisplayValue}
        width={150}
        alignment={"center"}
      />
      <Column
        dataField={"kStartDate"}
        // caption="계약시작일"
        caption="Contract Start Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={150}
        alignment={"center"}
      />
      <Column
        dataField={"kEndDate"}
        // caption="계약종료일"
        caption="Contract End Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={150}
        alignment={"center"}
      />
      <Column
        dataField={"probationaryStartDate"}
        // caption="수습시작일"
        caption="Probation Start Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={150}
        alignment={"center"}
      />
      <Column
        dataField={"probationaryEndDate"}
        // caption="수습종료일"
        caption="Probation End Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={150}
        alignment={"center"}
      />
      <Column type={"buttons"} width={80} />
    </DataGrid>
  );
}
